import { FilterContextData } from 'contexts/FilterContext';
import type { WhitespaceGridRow } from 'hooks/useWhitespaceAnalysisGridData';
import { useCallback, useContext, useMemo, useRef } from 'react';
import { trackEventMixpanel } from 'utils/mixpanel';
import {
    PPA_WHITESPACE_AXES_MENU_OPENED,
    PPA_WHITESPACE_CROSS_SECTION_OPENED,
    PPA_WHITESPACE_CROSS_SECTION_SELECTED,
    PPA_WHITESPACE_X_AXIS_SELECTED,
    PPA_WHITESPACE_Y_AXIS_SELECTED,
} from 'utils/mixpanelEventNames';
import {
    buildWhitespaceMixpanelBaseProps,
    computeWhitespaceGridMetrics,
    getWhitespaceComparisonMode,
    getWhitespaceEntryPoint,
} from './whitespaceMixpanel.helpers';
import useWhitespaceMixpanelEffects from './useWhitespaceMixpanelEffects';

type UseWhitespaceMixpanelParams = {
    isMetaLoading: boolean;
    isGridFetched: boolean;
    isGridFetching: boolean;
    xAxis: string;
    yAxis: string;
    crossSection: string;
    showWhitespaces: boolean;
    xAxisOptions: string[];
    yAxisOptions: string[];
    crossSectionOptions: string[];
    columnHeaders: string[];
    gridRows: WhitespaceGridRow[];
    selectedYAxisKpiId: string;
};

const useWhitespaceMixpanel = ({
    isMetaLoading,
    isGridFetched,
    isGridFetching,
    xAxis,
    yAxis,
    crossSection,
    showWhitespaces,
    xAxisOptions,
    yAxisOptions,
    crossSectionOptions,
    columnHeaders,
    gridRows,
    selectedYAxisKpiId,
}: UseWhitespaceMixpanelParams) => {
    const { allCheckedAttribute, ppaPersistedScope, timeSelectionLabel, timePeriodSelection } =
        useContext(FilterContextData);

    const configRefreshStartedAtRef = useRef<number | null>(null);
    const hasTrackedCellCardsViewedRef = useRef(false);

    const baseProps = useMemo(
        () =>
            buildWhitespaceMixpanelBaseProps({
                ppaPersistedScope,
                allCheckedAttribute,
                timeSelectionLabel,
                timePeriodSelection,
            }),
        [allCheckedAttribute, ppaPersistedScope, timePeriodSelection, timeSelectionLabel],
    );

    const gridMetrics = useMemo(
        () => computeWhitespaceGridMetrics(gridRows, columnHeaders),
        [columnHeaders, gridRows],
    );

    const comparisonMode = useMemo(
        () => getWhitespaceComparisonMode(gridRows, columnHeaders),
        [columnHeaders, gridRows],
    );

    const pageViewProps = useMemo(
        () => ({
            ...baseProps,
            entry_point: getWhitespaceEntryPoint(),
            x_axis: xAxis,
            y_axis: yAxis,
            cross_section: crossSection,
            show_whitespaces: showWhitespaces,
            comparison_mode: comparisonMode,
        }),
        [baseProps, comparisonMode, crossSection, showWhitespaces, xAxis, yAxis],
    );

    useWhitespaceMixpanelEffects({
        isMetaLoading,
        isGridFetched,
        isGridFetching,
        xAxis,
        yAxis,
        crossSection,
        showWhitespaces,
        gridRows,
        baseProps,
        gridMetrics,
        pageViewProps,
        configRefreshStartedAtRef,
        hasTrackedCellCardsViewedRef,
    });

    const markConfigRefreshStart = useCallback(() => {
        configRefreshStartedAtRef.current = performance.now();
        hasTrackedCellCardsViewedRef.current = false;
    }, []);

    const trackAxesMenuOpened = useCallback(() => {
        trackEventMixpanel(PPA_WHITESPACE_AXES_MENU_OPENED, {
            current_x_axis: xAxis,
            current_y_axis: yAxis,
            available_x_axes: xAxisOptions,
            available_y_axes: yAxisOptions,
            ...baseProps,
        });
    }, [baseProps, xAxis, xAxisOptions, yAxis, yAxisOptions]);

    const trackXAxisSelected = useCallback(
        (previousXAxis: string, selectedXAxis: string) => {
            trackEventMixpanel(PPA_WHITESPACE_X_AXIS_SELECTED, {
                previous_x_axis: previousXAxis,
                selected_x_axis: selectedXAxis,
                available_x_axes: xAxisOptions,
                ...baseProps,
            });
        },
        [baseProps, xAxisOptions],
    );

    const trackYAxisSelected = useCallback(
        (previousYAxis: string, selectedYAxis: string) => {
            trackEventMixpanel(PPA_WHITESPACE_Y_AXIS_SELECTED, {
                previous_y_axis: previousYAxis,
                selected_y_axis: selectedYAxis,
                y_axis_value_source: selectedYAxisKpiId,
                ...baseProps,
            });
        },
        [baseProps, selectedYAxisKpiId],
    );

    const trackCrossSectionOpened = useCallback(() => {
        trackEventMixpanel(PPA_WHITESPACE_CROSS_SECTION_OPENED, {
            current_cross_section: crossSection,
            available_cross_sections: crossSectionOptions,
            ...baseProps,
        });
    }, [baseProps, crossSection, crossSectionOptions]);

    const trackCrossSectionSelected = useCallback(
        (previousCrossSection: string, selectedCrossSection: string) => {
            trackEventMixpanel(PPA_WHITESPACE_CROSS_SECTION_SELECTED, {
                previous_cross_section: previousCrossSection,
                selected_cross_section: selectedCrossSection,
                ...baseProps,
            });
        },
        [baseProps],
    );

    return {
        baseProps,
        gridMetrics,
        comparisonMode,
        markConfigRefreshStart,
        trackAxesMenuOpened,
        trackXAxisSelected,
        trackYAxisSelected,
        trackCrossSectionOpened,
        trackCrossSectionSelected,
    };
};

export default useWhitespaceMixpanel;
